/**
 * Activity Tracker Hook
 * Tracks user activity and extends session automatically.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from './useAuthApi';
import authService from '@/lib/api/auth';

interface UseActivityTrackerOptions {
  /**
   * Throttle time in milliseconds between session extension calls
   * Default: 5 minutes (300000ms)
   */
  throttleTime?: number;
  
  /**
   * Events to track for activity
   * Default: ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart']
   */
  events?: string[];
  
  /**
   * Whether to track activity automatically
   * Default: true
   */
  enabled?: boolean;
}

export const useActivityTracker = (options: UseActivityTrackerOptions = {}) => {
  const {
    throttleTime = 5 * 60 * 1000, // 5 minutes
    events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'],
    enabled = true,
  } = options;

  const { user } = useAuth();
  const lastExtensionRef = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const extendSession = async () => {
    const now = Date.now();
    const timeSinceLastExtension = now - lastExtensionRef.current;

    // Only extend if enough time has passed and user is authenticated
    if (timeSinceLastExtension >= throttleTime && user) {
      try {
        lastExtensionRef.current = now;
        await authService.extendSession();
        console.log('Session extended due to user activity');
      } catch (error) {
        console.error('Failed to extend session:', error);
      }
    }
  };

  const handleActivity = () => {
    if (!enabled || !user) return;

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce the session extension call
    timeoutRef.current = setTimeout(() => {
      extendSession();
    }, 1000); // Wait 1 second of inactivity before extending
  };

  useEffect(() => {
    if (!enabled || !user) return;

    // Add event listeners for activity tracking
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Cleanup function
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [enabled, user, throttleTime]);

  // Return utility functions
  return {
    /**
     * Manually extend the session
     */
    extendSessionNow: extendSession,
    
    /**
     * Get time since last extension
     */
    getTimeSinceLastExtension: () => Date.now() - lastExtensionRef.current,
  };
};

export default useActivityTracker;